import React, { useEffect, useState } from 'react';
import { 
  Building2, 
  User, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin, 
  Target, 
  Users, 
  DollarSign,
  BarChart2,
  ArrowUp,
  ArrowDown,
  Clock
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from 'axios';

export default function NGODashboard() {
  const navigate = useNavigate();
  const [ngoData, setNGOData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 4,
    activeProjects: 2,
    totalDonations: 175000,
    totalVolunteers: 28,
    recentActivities: [
      { id: 1, type: 'donation', amount: 5000, name: 'Rahul Sharma', time: '2 hours ago' },
      { id: 2, type: 'volunteer', name: 'Priya Patel', project: 'Education for All', time: '5 hours ago' },
      { id: 3, type: 'donation', amount: 10000, name: 'Amit Kumar', time: '1 day ago' },
      { id: 4, type: 'project', name: 'Clean Water for Villages', status: 'created', time: '2 days ago' },
    ],
    upcomingEvents: [
      { id: 1, title: 'Volunteer Training', date: '2025-05-15', location: 'Delhi NCR' },
      { id: 2, title: 'Fundraising Dinner', date: '2025-05-20', location: 'Mumbai' },
    ]
  });

  useEffect(() => {
    // Check if user is logged in and is an NGO
    const token = localStorage.getItem('ngoToken');
    const ngoUser = JSON.parse(localStorage.getItem('ngoUser') || '{}');
    const userType = localStorage.getItem('userType');
    
    if (!token || !ngoUser || userType !== 'ngo') {
      toast.error('Please login as an NGO to access this page');
      navigate('/ngo/login');
      return;
    }
    
    // Use the NGO data from localStorage instead of making API calls
    const fetchNGOProfile = async () => {
      try {
        // Get NGO data from localStorage
        const ngoUser = JSON.parse(localStorage.getItem('ngoUser') || '{}');
        
        // If we have NGO data in localStorage, use it
        if (ngoUser && Object.keys(ngoUser).length > 0) {
          setNGOData(ngoUser);
          setLoading(false);
          return;
        }
        
        // Fallback to mock data if localStorage doesn't have NGO data
        const mockNGOData = {
          _id: '6816bca396d21547ee8245',
          name: 'Helping Hands Foundation',
          email: 'info@helpinghands.org',
          phone: '9166757213',
          address: 'Yamuna Nagar, Haryana',
          description: 'We are dedicated to providing support to underprivileged communities through various initiatives.',
          status: 'approved',
          createdAt: '2025-04-10T10:30:00.000Z',
          logo: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
        };
        
        setNGOData(mockNGOData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching NGO profile:', error);
        toast.error('Failed to load NGO profile');
        setLoading(false);
      }
    };

    fetchNGOProfile();
  }, [navigate]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Section */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          Welcome, {ngoData.name}!
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Here's an overview of your NGO's activities and performance
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Projects Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Projects</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalProjects}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full dark:bg-blue-900">
              <Target className="h-6 w-6 text-blue-600 dark:text-blue-300" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-green-500 flex items-center">
              <ArrowUp className="h-4 w-4 mr-1" />
              {stats.activeProjects}
            </span>
            <span className="ml-2 text-gray-600 dark:text-gray-400">Active projects</span>
          </div>
        </div>

        {/* Donations Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Donations</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(stats.totalDonations)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full dark:bg-green-900">
              <DollarSign className="h-6 w-6 text-green-600 dark:text-green-300" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-green-500 flex items-center">
              <ArrowUp className="h-4 w-4 mr-1" />
              12%
            </span>
            <span className="ml-2 text-gray-600 dark:text-gray-400">From last month</span>
          </div>
        </div>

        {/* Volunteers Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Volunteers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalVolunteers}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full dark:bg-purple-900">
              <Users className="h-6 w-6 text-purple-600 dark:text-purple-300" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-green-500 flex items-center">
              <ArrowUp className="h-4 w-4 mr-1" />
              8%
            </span>
            <span className="ml-2 text-gray-600 dark:text-gray-400">New volunteers</span>
          </div>
        </div>

        {/* Events Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Upcoming Events</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.upcomingEvents.length}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full dark:bg-yellow-900">
              <Calendar className="h-6 w-6 text-yellow-600 dark:text-yellow-300" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-600 dark:text-gray-400 flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Next: {stats.upcomingEvents.length > 0 ? formatDate(stats.upcomingEvents[0].date) : 'None'}
            </span>
          </div>
        </div>
      </div>

      {/* Projects and Activities Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 lg:col-span-1">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Quick Actions</h2>
          <div className="space-y-4">
            <Link to="/ngo/dashboard/projects/create" className="flex items-center p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200 dark:bg-blue-900 dark:hover:bg-blue-800">
              <div className="p-2 bg-blue-200 rounded-full mr-3 dark:bg-blue-700">
                <Target className="h-5 w-5 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <p className="font-medium text-blue-600 dark:text-blue-300">Create New Project</p>
                <p className="text-xs text-blue-500 dark:text-blue-400">Start a new campaign or initiative</p>
              </div>
            </Link>
            
            <Link to="/ngo/dashboard/events/create" className="flex items-center p-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors duration-200 dark:bg-yellow-900 dark:hover:bg-yellow-800">
              <div className="p-2 bg-yellow-200 rounded-full mr-3 dark:bg-yellow-700">
                <Calendar className="h-5 w-5 text-yellow-600 dark:text-yellow-300" />
              </div>
              <div>
                <p className="font-medium text-yellow-600 dark:text-yellow-300">Schedule Event</p>
                <p className="text-xs text-yellow-500 dark:text-yellow-400">Plan a new event or workshop</p>
              </div>
            </Link>
            
            <Link to="/ngo/dashboard/volunteers" className="flex items-center p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200 dark:bg-purple-900 dark:hover:bg-purple-800">
              <div className="p-2 bg-purple-200 rounded-full mr-3 dark:bg-purple-700">
                <Users className="h-5 w-5 text-purple-600 dark:text-purple-300" />
              </div>
              <div>
                <p className="font-medium text-purple-600 dark:text-purple-300">Manage Volunteers</p>
                <p className="text-xs text-purple-500 dark:text-purple-400">View and organize volunteer activities</p>
              </div>
            </Link>
            
            <Link to="/ngo/dashboard/reports" className="flex items-center p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200 dark:bg-green-900 dark:hover:bg-green-800">
              <div className="p-2 bg-green-200 rounded-full mr-3 dark:bg-green-700">
                <BarChart2 className="h-5 w-5 text-green-600 dark:text-green-300" />
              </div>
              <div>
                <p className="font-medium text-green-600 dark:text-green-300">Generate Reports</p>
                <p className="text-xs text-green-500 dark:text-green-400">Create financial and activity reports</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 lg:col-span-2">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Recent Activities</h2>
          <div className="space-y-4">
            {stats.recentActivities.map(activity => (
              <div key={activity.id} className="flex items-start border-b border-gray-100 dark:border-gray-700 pb-4">
                <div className={`p-2 rounded-full mr-3 ${activity.type === 'donation' ? 'bg-green-100 dark:bg-green-900' : activity.type === 'volunteer' ? 'bg-purple-100 dark:bg-purple-900' : 'bg-blue-100 dark:bg-blue-900'}`}>
                  {activity.type === 'donation' ? (
                    <DollarSign className="h-5 w-5 text-green-600 dark:text-green-300" />
                  ) : activity.type === 'volunteer' ? (
                    <Users className="h-5 w-5 text-purple-600 dark:text-purple-300" />
                  ) : (
                    <Target className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      {activity.type === 'donation' ? (
                        <p className="font-medium text-gray-900 dark:text-white">
                          New donation of {formatCurrency(activity.amount)}
                        </p>
                      ) : activity.type === 'volunteer' ? (
                        <p className="font-medium text-gray-900 dark:text-white">
                          New volunteer joined: {activity.project}
                        </p>
                      ) : (
                        <p className="font-medium text-gray-900 dark:text-white">
                          Project {activity.status}: {activity.name}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {activity.type === 'donation' || activity.type === 'volunteer' ? activity.name : ''}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <button className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
              View All Activities
            </button>
          </div>
        </div>
      </div>

      {/* Upcoming Events Section */}
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Upcoming Events</h2>
        {stats.upcomingEvents.length > 0 ? (
          <div className="space-y-4">
            {stats.upcomingEvents.map(event => (
              <div key={event.id} className="flex items-center border-b border-gray-100 dark:border-gray-700 pb-4 last:border-0 last:pb-0">
                <div className="p-3 bg-yellow-100 rounded-lg mr-4 dark:bg-yellow-900 text-center min-w-[60px]">
                  <p className="text-xs text-yellow-600 dark:text-yellow-300">
                    {new Date(event.date).toLocaleDateString(undefined, { month: 'short' })}
                  </p>
                  <p className="text-lg font-bold text-yellow-600 dark:text-yellow-300">
                    {new Date(event.date).getDate()}
                  </p>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white">{event.title}</h3>
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mt-1">
                    <MapPin className="h-4 w-4 mr-1" />
                    {event.location}
                  </div>
                </div>
                <button className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800">
                  Details
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400">No upcoming events scheduled.</p>
        )}
        <div className="mt-4 text-center">
          <Link to="/ngo/dashboard/events" className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
            View All Events
          </Link>
        </div>
      </div>
    </div>
  );
}
